// Settings management module for PPT Hero game
export default class SettingsManager {
  constructor(scene) {
    this.scene = scene;
    this.defaultSettings = {
      soundEnabled: true,
      musicEnabled: true,
      volume: 0.7,
      difficulty: 'normal',
      autoSave: true,
      showHints: true,
      showTimer: true,
      showScore: true,
      language: 'en',
      theme: 'dark',
      accessibility: {
        highContrast: false,
        largeText: false,
        screenReader: false
      }
    };
    this.settings = this.loadSettings();
  }

  // Load settings from localStorage
  loadSettings() {
    try {
      const savedSettings = localStorage.getItem('pptHeroSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return { ...this.defaultSettings, ...parsed };
      }
      return { ...this.defaultSettings };
    } catch (error) {
      console.error('Error loading settings:', error);
      return { ...this.defaultSettings };
    }
  }

  // Save settings to localStorage
  saveSettings() {
    try {
      localStorage.setItem('pptHeroSettings', JSON.stringify(this.settings));
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }

  // Get a setting value
  getSetting(key) {
    return this.settings[key];
  }

  // Set a setting value
  setSetting(key, value) {
    this.settings[key] = value;
    this.saveSettings();
    this.applySetting(key, value);
  }

  // Apply a setting change
  applySetting(key, value) {
    try {
      switch (key) {
        case 'soundEnabled':
          this.applySoundSetting(value);
          break;
        case 'musicEnabled':
          this.applyMusicSetting(value);
          break;
        case 'volume':
          this.applyVolumeSetting(value);
          break;
        case 'difficulty':
          this.applyDifficultySetting(value);
          break;
        case 'showHints':
          this.applyHintsSetting(value);
          break;
        case 'showTimer':
          this.applyTimerSetting(value);
          break;
        case 'showScore':
          this.applyScoreSetting(value);
          break;
        case 'theme':
          this.applyThemeSetting(value);
          break;
        case 'accessibility':
          this.applyAccessibilitySettings(value);
          break;
      }
    } catch (error) {
      console.error('Error applying setting:', key, value, error);
    }
  }

  // Apply sound setting
  applySoundSetting(enabled) {
    if (this.scene.sound) {
      this.scene.sound.setMute(!enabled);
    }
  }

  // Apply music setting
  applyMusicSetting(enabled) {
    if (this.scene.music) {
      if (enabled) {
        this.scene.music.resume();
      } else {
        this.scene.music.pause();
      }
    }
  }

  // Apply volume setting
  applyVolumeSetting(volume) {
    if (this.scene.sound) {
      this.scene.sound.setVolume(volume);
    }
    if (this.scene.music) {
      this.scene.music.setVolume(volume * 0.5); // Music at half volume
    }
  }

  // Apply difficulty setting
  applyDifficultySetting(difficulty) {
    // Adjust game parameters based on difficulty
    switch (difficulty) {
      case 'easy':
        this.scene.timeMultiplier = 1.5;
        this.scene.scoreMultiplier = 0.8;
        break;
      case 'normal':
        this.scene.timeMultiplier = 1.0;
        this.scene.scoreMultiplier = 1.0;
        break;
      case 'hard':
        this.scene.timeMultiplier = 0.7;
        this.scene.scoreMultiplier = 1.3;
        break;
      case 'expert':
        this.scene.timeMultiplier = 0.5;
        this.scene.scoreMultiplier = 1.5;
        break;
    }
  }

  // Apply hints setting
  applyHintsSetting(show) {
    if (this.scene.hintText) {
      this.scene.hintText.setVisible(show);
    }
  }

  // Apply timer setting
  applyTimerSetting(show) {
    if (this.scene.timerDisplay) {
      this.scene.timerDisplay.setVisible(show);
    }
  }

  // Apply score setting
  applyScoreSetting(show) {
    if (this.scene.scoreText) {
      this.scene.scoreText.setVisible(show);
    }
  }

  // Apply theme setting
  applyThemeSetting(theme) {
    const colors = this.getThemeColors(theme);
    
    // Apply theme colors to UI elements
    if (this.scene.background) {
      this.scene.background.setFillStyle(colors.background);
    }
    
    if (this.scene.sidebar) {
      this.scene.sidebar.setFillStyle(colors.sidebar);
    }
    
    // Update text colors
    this.updateTextColors(colors.text);
  }

  // Get theme colors
  getThemeColors(theme) {
    switch (theme) {
      case 'light':
        return {
          background: 0xf0f0f0,
          sidebar: 0xe0e0e0,
          text: '#000000',
          accent: '#007acc'
        };
      case 'dark':
      default:
        return {
          background: 0x1a1a1a,
          sidebar: 0x2a2a2a,
          text: '#ffffff',
          accent: '#00d4ff'
        };
    }
  }

  // Update text colors
  updateTextColors(color) {
    // Update various text elements
    const textElements = [
      this.scene.titleText,
      this.scene.scoreText,
      this.scene.timerText,
      this.scene.instructionText
    ];
    
    textElements.forEach(element => {
      if (element && element.setColor) {
        element.setColor(color);
      }
    });
  }

  // Apply accessibility settings
  applyAccessibilitySettings(accessibility) {
    if (accessibility.highContrast) {
      this.applyHighContrastMode();
    }
    
    if (accessibility.largeText) {
      this.applyLargeTextMode();
    }
    
    if (accessibility.screenReader) {
      this.enableScreenReaderSupport();
    }
  }

  // Apply high contrast mode
  applyHighContrastMode() {
    // Increase contrast for better visibility
    const highContrastColors = {
      background: 0x000000,
      sidebar: 0x000000,
      text: '#ffffff',
      accent: '#ffff00'
    };
    
    this.applyThemeSetting('highContrast');
  }

  // Apply large text mode
  applyLargeTextMode() {
    const textElements = [
      this.scene.titleText,
      this.scene.scoreText,
      this.scene.timerText,
      this.scene.instructionText
    ];
    
    textElements.forEach(element => {
      if (element && element.setFontSize) {
        const currentSize = parseInt(element.style.fontSize);
        element.setFontSize(`${currentSize * 1.2}px`);
      }
    });
  }

  // Enable screen reader support
  enableScreenReaderSupport() {
    // Add ARIA labels and descriptions
    if (this.scene.canvas) {
      this.scene.canvas.setAttribute('role', 'application');
      this.scene.canvas.setAttribute('aria-label', 'PPT Hero Game');
    }
  }

  // Reset settings to defaults
  resetSettings() {
    this.settings = { ...this.defaultSettings };
    this.saveSettings();
    
    // Apply all default settings
    Object.entries(this.settings).forEach(([key, value]) => {
      this.applySetting(key, value);
    });
    
    console.log('Settings reset to defaults');
  }

  // Export settings
  exportSettings() {
    return {
      settings: this.settings,
      exportDate: new Date().toISOString()
    };
  }

  // Import settings
  importSettings(data) {
    try {
      if (data.settings) {
        this.settings = { ...this.defaultSettings, ...data.settings };
        this.saveSettings();
        
        // Apply all imported settings
        Object.entries(this.settings).forEach(([key, value]) => {
          this.applySetting(key, value);
        });
        
        console.log('Settings imported successfully');
        return true;
      }
    } catch (error) {
      console.error('Error importing settings:', error);
    }
    return false;
  }

  // Get all settings
  getAllSettings() {
    return { ...this.settings };
  }

  // Check if a setting is enabled
  isEnabled(key) {
    return this.settings[key] === true;
  }

  // Toggle a boolean setting
  toggleSetting(key) {
    if (typeof this.settings[key] === 'boolean') {
      this.setSetting(key, !this.settings[key]);
    }
  }
}



